import { Box, Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Main hero container
 * Flex layout with promotional panel on left and order details on right
 */
export const HeroContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(6),
  alignItems: 'stretch',
  borderRadius: theme.customShape.borderRadius3XL,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.contrast,
  border: `1px solid ${theme.palette.info.main}`,
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    gap: theme.spacing(0),
  },
}));

/**
 * Promotional panel container
 * Left side with gradient background
 * Always 30% of parent width, with SVG anchored to right side
 */
export const PromotionalPanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(10),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '30%',
  flexShrink: 0,
  background: theme.palette.background.hero,
  [theme.breakpoints.up('xl')]: {
    width: '25%',
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    padding: theme.spacing(6, 0),
  },
}));

/**
 * Promotional text container
 * Groups title and subtitle
 */
export const PromotionalText = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  color: theme.palette.common.white,
  alignItems: 'center',
  maxWidth: '100%',
}));

/**
 * Promotional title
 * Large bold text
 */
export const PromotionalTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h4,
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(36),
  lineHeight: 1,
  color: theme.palette.common.white,
  textAlign: 'left',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(24),
    textAlign: 'center',
  },
}));

/**
 * Promotional subtitle
 * Regular body text
 */
export const PromotionalSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle1,
  fontSize: theme.typography.pxToRem(18),
  lineHeight: 1.4,
  color: theme.palette.common.white,
  textAlign: 'left',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(16),
    textAlign: 'center',
  },
}));

/**
 * Order details container
 * Right side with order information
 */
export const OrderDetailsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  padding: theme.spacing(3, 10, 0),
  flex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingTop: theme.spacing(0),
  },
}));

/**
 * Continue to payment button
 * Primary action button with cart icon
 */
export const ContinueButtonContainer = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  backgroundColor: theme.palette.secondaryInvert.main,
  color: theme.palette.common.white,
  borderRadius: theme.spacing(20),
  padding: theme.spacing(3, 6),
  textTransform: 'none',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginLeft: theme.spacing(6),
  marginRight: theme.spacing(6),
  width: 'auto',
  [theme.breakpoints.down('lg')]: {
    height: theme.spacing(9),
    padding: theme.spacing(3, 4),
  },
}));

/**
 * Button left section
 * Groups icon and text
 */
export const ButtonContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

/**
 * Button text
 * Regular weight text
 */
export const ButtonText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  fontSize: theme.typography.pxToRem(18),
  color: theme.palette.common.white,
  fontWeight: theme.typography.fontWeightRegular,
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(16),
  },
}));
